import api, { ApiResponse } from './api';

// Studio projects: the creative-canvas entity (the `projects` table), not the tenant workspace.
export interface ProjectSummary {
  id: string;
  name: string;
  description: string | null;
  activeTool: string;
  assetCount: number;
  thumbnailPath: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectAsset {
  id: string;
  type: 'image' | 'video';
  dataUrl: string | null;
  gcsPath: string;
  mimeType: string;
  prompt: string | null;
  description: string | null;
  tool: string;
  position: number;
  createdAt: string;
}

export interface ProjectDetail {
  id: string;
  name: string;
  description: string | null;
  activeTool: string;
  activeAssetId: string | null;
  assets: ProjectAsset[];
  pendingVideoOps: Record<string, string>;
  canvasState: { nodes: unknown[]; edges: unknown[] } | null;
  createdAt: string;
  updatedAt: string;
}

export const projectsService = {
  list: async (offset = 0, limit = 50): Promise<{ data: ProjectSummary[]; total: number }> => {
    const response = await api.get<ApiResponse<ProjectSummary[]>>('/studio/projects', {
      params: { offset, limit },
    });
    return { data: response.data.data!, total: response.data._meta?.total ?? 0 };
  },

  create: async (name: string, workspace_id: string, description?: string): Promise<ProjectDetail> => {
    const response = await api.post<ApiResponse<ProjectDetail>>('/studio/projects', {
      name,
      description,
      workspace_id,
    });
    return response.data.data!;
  },

  get: async (id: string): Promise<ProjectDetail> => {
    const response = await api.get<ApiResponse<ProjectDetail>>(`/studio/projects/${id}`);
    return response.data.data!;
  },

  update: async (
    id: string,
    data: {
      name?: string;
      description?: string;
      activeTool?: string;
      activeAssetId?: string | null;
      pendingVideoOps?: Record<string, string>;
      canvasState?: { nodes: unknown[]; edges: unknown[] };
    }
  ): Promise<void> => {
    await api.put(`/studio/projects/${id}`, data);
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/studio/projects/${id}`);
  },

  addAsset: async (
    projectId: string,
    asset: {
      type: 'image' | 'video';
      data: string;
      mimeType: string;
      prompt?: string;
      description?: string;
      tool: string;
    }
  ): Promise<ProjectAsset> => {
    const response = await api.post<ApiResponse<ProjectAsset>>(
      `/studio/projects/${projectId}/assets`,
      asset
    );
    return response.data.data!;
  },

  removeAsset: async (projectId: string, assetId: string): Promise<void> => {
    await api.delete(`/studio/projects/${projectId}/assets/${assetId}`);
  },
};
